import React, { useEffect, useMemo, useState } from "react";

import { ChevronDown } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import DropdownSelector from "../../../../Components/DropdownSelector";
import TabsComponent from "../../../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import { fetchAssessmentTrendline } from "../../../../Store/Reducers/AccountWorkspaceSlice";
import { fetchOrganizationLevels } from "../../../../Store/Reducers/AdminSlice";
import { parseLevelNumber } from "../../../../Utils/Helper/levelUtils";

type AssessmentsTrendsCardProps = {
  accountId: string | undefined;
  className?: string;
};

type OrganizationLevel = {
  level_code: string;
  name: string;
};

type TrendPeriod = "3m" | "5m" | "1y";

type ChartPoint = {
  name: string;
  value: number;
  publishedDate: string;
};

const ASSESSMENT_TABS = [
  { id: "al", label: "AL Assessments" },
  { id: "rl", label: "RL Assessment", disabled: true },
  { id: "rl-vs-ai", label: "RL vs AI Score", disabled: true },
];

const PERIOD_OPTIONS: { label: string; value: TrendPeriod }[] = [
  { label: "3 Months Ago", value: "3m" },
  { label: "5 Months Ago", value: "5m" },
  { label: "1 Year Ago", value: "1y" },
];

const TREND_PERIOD_LABELS: Record<TrendPeriod, string> = {
  "3m": "3 Months Ago",
  "5m": "5 Months Ago",
  "1y": "1 Year Ago",
};

const LEVEL_TREND_COLORS = [
  "#95A5A6",
  "#E74C3C",
  "#F39E0B",
  "#2ECC71",
  "#27AE60",
  "#1E8449",
  "#145A32",
  "#0B3D1E",
];

const LINE_STROKE = "#7086FD";

const getTrendPeriodLabel = (period: TrendPeriod): string =>
  TREND_PERIOD_LABELS[period];

const getTrendDateRange = (
  period: TrendPeriod
): { from_date: string; to_date: string } => {
  const to = new Date();
  const from = new Date();

  if (period === "3m") {
    from.setMonth(from.getMonth() - 3);
  } else if (period === "5m") {
    from.setMonth(from.getMonth() - 5);
  } else {
    from.setFullYear(from.getFullYear() - 1);
  }

  const format = (d: Date) => d.toISOString().slice(0, 10);
  return { from_date: format(from), to_date: format(to) };
};

const formatTrendMonthLabel = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-US", { month: "short" });
};

const getLevelTrendColor = (level: string | number): string => {
  const n = parseLevelNumber(level);
  return LEVEL_TREND_COLORS[Math.min(n, LEVEL_TREND_COLORS.length - 1)];
};

const buildSortedLevelTicks = (
  organizationLevels: OrganizationLevel[]
): number[] => {
  const fromOrg = organizationLevels
    .map(l => parseLevelNumber(l.level_code))
    .filter(n => Number.isFinite(n) && n >= 0);
  const maxFromOrg = fromOrg.length ? Math.max(...fromOrg) : 5;
  const max = Math.max(5, maxFromOrg);
  // Continuous 0…max so missing org codes (e.g. no L4) still appear on the axis
  return Array.from({ length: max + 1 }, (_, i) => i);
};

const mapTrendlineToChartData = (
  points: { published_date: string; level_number: number }[]
): ChartPoint[] =>
  [...points]
    .sort(
      (a, b) =>
        new Date(a.published_date).getTime() -
        new Date(b.published_date).getTime()
    )
    .map(point => ({
      name: formatTrendMonthLabel(point.published_date),
      value: point.level_number,
      publishedDate: point.published_date,
    }));

const AssessmentsTrendsCard: React.FC<AssessmentsTrendsCardProps> = ({
  accountId,
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const organizationLevels = useAppSelector(
    state => state.admin.organizationLevels
  );
  const {
    assessmentTrendline,
    assessmentTrendlineLoading,
    assessmentTrendlineError,
  } = useAppSelector(state => state.accountWorkspace);

  const [activeTab, setActiveTab] = useState("al");
  const [period, setPeriod] = useState<TrendPeriod>("5m");

  useEffect(() => {
    if (organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, organizationLevels.length]);

  useEffect(() => {
    if (!accountId) return;
    const { from_date, to_date } = getTrendDateRange(period);
    dispatch(fetchAssessmentTrendline({ accountId, from_date, to_date }));
  }, [accountId, period, dispatch]);

  const chartData = useMemo<ChartPoint[]>(
    () => mapTrendlineToChartData(assessmentTrendline?.points ?? []),
    [assessmentTrendline?.points]
  );

  const levelTicks = useMemo(
    () => buildSortedLevelTicks(organizationLevels),
    [organizationLevels]
  );

  const yDomain = useMemo<[number, number]>(() => {
    const min = levelTicks[0] ?? 0;
    const max = levelTicks[levelTicks.length - 1] ?? 5;
    return [min, max];
  }, [levelTicks]);

  const legend = useMemo(() => {
    const name = assessmentTrendline?.account_name?.trim();
    return name ? `With ${name}` : "Assessment level trend";
  }, [assessmentTrendline?.account_name]);

  const hasPoints = chartData.length > 0;

  return (
    <section
      className={`box-border flex min-h-[280px] w-full min-w-0 flex-col rounded-lg border border-[#EBEBEB] bg-white px-4 pb-3 pt-3.5 ${className}`}
    >
      <h2 className="m-0 text-base font-bold text-black">
        Assessments & Trends
      </h2>
      <hr className="m-0 mb-3 mt-3 w-full border-0 border-t border-[#F6F6F6]" />

      <TabsComponent
        tabs={ASSESSMENT_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        variant="tab-underline"
      />

      {activeTab === "al" && (
        <div className="mt-3 flex min-h-0 flex-1 flex-col">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold text-[#4D4D4D]">
                Trend line
              </span>
              <ChevronDown className="size-4 text-[#9F9F9F]" aria-hidden />
            </div>
            <DropdownSelector
              label="Time Period"
              selected={period}
              options={PERIOD_OPTIONS}
              onSelect={value => setPeriod(value as TrendPeriod)}
            />
          </div>

          {assessmentTrendlineLoading && (
            <p className="m-0 flex flex-1 items-center justify-center text-sm text-[#9F9F9F]">
              Loading trend…
            </p>
          )}

          {!assessmentTrendlineLoading && assessmentTrendlineError && (
            <p className="m-0 text-sm text-[#E01E21]" role="alert">
              {assessmentTrendlineError}
            </p>
          )}

          {!assessmentTrendlineLoading &&
            !assessmentTrendlineError &&
            !hasPoints && (
              <p className="m-0 flex flex-1 items-center justify-center text-sm text-[#9F9F9F]">
                No assessment trend data for{" "}
                {getTrendPeriodLabel(period).toLowerCase()}.
              </p>
            )}

          {!assessmentTrendlineLoading &&
            !assessmentTrendlineError &&
            hasPoints && (
              <>
                <div className="min-h-[200px] w-full">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e0e6ed"
                        vertical
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#4D4D4D" }}
                      />
                      <YAxis
                        domain={yDomain}
                        ticks={levelTicks}
                        interval={0}
                        width={28}
                        axisLine={false}
                        tickLine={false}
                        tick={({ x, y, payload }) => (
                          <g transform={`translate(${x},${y})`}>
                            <circle
                              cx={-10}
                              cy={0}
                              r={5}
                              fill={getLevelTrendColor(payload.value)}
                            />
                          </g>
                        )}
                      />
                      <Line
                        type="linear"
                        dataKey="value"
                        stroke={LINE_STROKE}
                        strokeWidth={1}
                        dot={({ cx, cy }) => (
                          <g>
                            {cx != null && cy != null && (
                              <>
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={8}
                                  fill={LINE_STROKE}
                                  opacity={0.2}
                                />
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={4}
                                  fill="white"
                                  stroke={LINE_STROKE}
                                  strokeWidth={2}
                                />
                              </>
                            )}
                          </g>
                        )}
                        activeDot={{
                          r: 7,
                          stroke: LINE_STROKE,
                          strokeWidth: 2,
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <span
                    className="size-2 shrink-0 rounded-full bg-[#C39EFF]"
                    aria-hidden
                  />
                  <span className="text-sm font-medium text-black/70">
                    {legend}
                  </span>
                </div>
              </>
            )}
        </div>
      )}
    </section>
  );
};

export default AssessmentsTrendsCard;
